import { forwardRef, useCallback, useState } from "react";
import { ChevronDownIcon, XIcon } from "lucide-react";
import { cn } from "src/shadcn/lib/utils";
import {
  Combobox,
  ComboboxContent,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxTrigger,
} from "src/primitives/Combobox";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { SelectAnchor } from "./SelectAnchor";
import type { SelectComboboxProps } from "./SelectCombobox.types";

export const SelectCombobox = forwardRef<HTMLInputElement, SelectComboboxProps>(
  (
    {
      name,
      id,
      effectiveItems,
      labelForValue,
      renderOption,
      renderTrigger,
      isSearchable,
      isAsync,
      isCreatable,
      creatableFilter,
      currentValue,
      handleValueChange,
      handleInputValueChange,
      handleClear,
      onOpenChange,
      isDisabled,
      autoHighlight,
      required,
      showClearButton,
      sizeConfig,
      error,
      placeholder,
      ariaDescribedBy,
      testIdPrefix,
      inputClassName,
      contentProps,
      dropdownProps,
      children,
    },
    ref
  ) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const handleClearClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        handleClear();
      },
      [handleClear]
    );

    return (
      // @ts-expect-error — conditional spread creates a union that TS cannot narrow to the Combobox discriminated-union props
      <Combobox
        name={name}
        {...(effectiveItems && !children
          ? {
              items: effectiveItems,
              itemToStringLabel: labelForValue as (val: unknown) => string,
              ...(!isSearchable || isAsync
                ? { filter: null }
                : isCreatable
                  ? { filter: creatableFilter }
                  : {}),
            }
          : {})}
        value={currentValue}
        onValueChange={handleValueChange as (next: unknown) => void}
        {...(isCreatable || isAsync
          ? { onInputValueChange: handleInputValueChange }
          : {})}
        onOpenChange={onOpenChange}
        disabled={isDisabled}
        autoHighlight={autoHighlight}
      >
        <SelectAnchor
          ref={setAnchorEl}
          isSearchable={isSearchable}
          dataTestId={testIdPrefix}
          className={!isSearchable ? "cursor-pointer" : undefined}
        >
          <ComboboxChips
            className={cn(
              sizeConfig.chips,
              !!error &&
                "border-destructive ring-3 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40",
              inputClassName
            )}
            {...(testIdPrefix
              ? { "data-testid": `${testIdPrefix}-select-container` }
              : {})}
          >
            {!isSearchable &&
              (currentValue ? (
                <span className="flex-1 truncate">
                  {renderTrigger(currentValue)}
                </span>
              ) : (
                <span className="flex-1 truncate text-muted-foreground">
                  {placeholder}
                </span>
              ))}
            <ComboboxChipsInput
              ref={ref}
              id={id}
              placeholder={
                isSearchable && !currentValue ? placeholder : undefined
              }
              aria-invalid={!!error || undefined}
              aria-describedby={ariaDescribedBy}
              aria-required={required || undefined}
              className={cn(
                sizeConfig.input,
                !isSearchable && "w-0 min-w-0 overflow-hidden p-0 opacity-0"
              )}
              disabled={isDisabled}
              {...(testIdPrefix
                ? { "data-testid": `${testIdPrefix}-select-input` }
                : {})}
            />
            {showClearButton && (
              <button
                type="button"
                tabIndex={-1}
                aria-label="Clear selection"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={handleClearClick}
              >
                <XIcon className={sizeConfig.icon} />
              </button>
            )}
            {isSearchable ? (
              <ComboboxTrigger
                className={cn(
                  "shrink-0 bg-transparent p-0 text-muted-foreground hover:bg-transparent",
                  sizeConfig.icon
                )}
              />
            ) : (
              <ChevronDownIcon
                aria-hidden
                className={cn(
                  "pointer-events-none shrink-0 text-muted-foreground",
                  sizeConfig.icon
                )}
              />
            )}
          </ComboboxChips>
        </SelectAnchor>

        <ComboboxContent anchor={anchorEl} {...contentProps}>
          <MultiSelectDropdown
            {...dropdownProps}
            renderOption={renderOption}
            testIdPrefix={testIdPrefix}
          >
            {children}
          </MultiSelectDropdown>
        </ComboboxContent>
      </Combobox>
    );
  }
);

SelectCombobox.displayName = "SelectCombobox";
